import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import Navbar from '../components/Navbar';
import Logo from '../components/Logo';
import SEO from '../components/SEO';
import { ArrowRight, TrendingUp, Users, Target, Zap } from 'lucide-react';

// Hook pour animations au scroll
const useInView = () => {
  const ref = useRef(null);
  const [isInView, setIsInView] = useState(false);

  useEffect(() => {
    const currentRef = ref.current;
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        setIsInView(true);
      }
    }, { threshold: 0.1 });

    if (currentRef) {
      observer.observe(currentRef);
    }

    return () => {
      if (currentRef) {
        observer.unobserve(currentRef);
      }
    };
  }, []);

  return [ref, isInView];
};

// Composant pour sections animées
const AnimatedSection = ({ children, delay = 0, className = "" }) => {
  const [ref, isInView] = useInView();
  
  return (
    <div 
      ref={ref}
      className={`transform transition-all duration-700 ${className}`}
      style={{ 
        transitionDelay: `${delay}ms`,
        opacity: isInView ? 1 : 0,
        transform: isInView ? 'translateY(0)' : 'translateY(30px)'
      }}
    >
      {children}
    </div>
  );
};

// Compteur animé
const AnimatedCounter = ({ end, suffix = '', duration = 2000 }) => {
  const [count, setCount] = useState(0);
  const [ref, isInView] = useInView();
  const hasAnimated = useRef(false);

  useEffect(() => {
    if (isInView && !hasAnimated.current) {
      hasAnimated.current = true;
      let startTime;
      const animate = (currentTime) => {
        if (!startTime) startTime = currentTime;
        const progress = Math.min((currentTime - startTime) / duration, 1);
        setCount(Math.floor(progress * end));
        if (progress < 1) {
          requestAnimationFrame(animate);
        }
      };
      requestAnimationFrame(animate);
    }
  }, [isInView, end, duration]);

  return <span ref={ref}>{count}{suffix}</span>;
};

const CaseStudies = () => {
  const { t, i18n } = useTranslation();

  // SEO data with translations
  const seoData = {
    fr: {
      title: 'Cas Pratiques & Success Stories - FOMOW | Résultats Clients B2B',
      description: 'Découvrez nos cas clients : +47% de CA moyen, cycles de vente x3, 10M€ générés. Transformations commerciales concrètes pour startups, scale-ups et PME.'
    },
    en: {
      title: 'Case Studies & Success Stories - FOMOW | B2B Client Results',
      description: 'Discover our case studies: +47% average revenue, 3x sales cycles, €10M generated. Concrete commercial transformations for startups, scale-ups and SMEs.'
    }
  };

  const currentSeo = seoData[i18n.language] || seoData.fr;

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    "name": currentSeo.title,
    "description": currentSeo.description,
    "url": "https://www.fomow.fr/case-studies"
  };

  // Stats impressionnantes
  const stats = [
    { value: 47, suffix: '%', labelKey: 'caseStudies.stat1Label' },
    { value: 3, suffix: 'x', labelKey: 'caseStudies.stat2Label' },
    { value: 89, suffix: '%', labelKey: 'caseStudies.stat3Label' },
    { value: 10, suffix: 'M€', labelKey: 'caseStudies.stat4Label' },
  ];

  // Teaser des cas clients (à venir) - cohérent avec les exemples de missions
  const teaserCards = [
    {
      icon: TrendingUp,
      sectorKey: 'caseStudies.case1Sector',
      titleKey: 'caseStudies.case1Title',
      descKey: 'caseStudies.case1Desc',
      tags: ['Architecture commerciale', 'Playbooks', 'CRM'],
    },
    {
      icon: Users,
      sectorKey: 'caseStudies.case2Sector',
      titleKey: 'caseStudies.case2Title',
      descKey: 'caseStudies.case2Desc',
      tags: ['Direction externalisée', 'Coaching managers', 'KPIs'],
    },
    {
      icon: Target,
      sectorKey: 'caseStudies.case3Sector',
      titleKey: 'caseStudies.case3Title',
      descKey: 'caseStudies.case3Desc',
      tags: ['Prospection', 'Recrutement', 'Formation'],
    },
    {
      icon: Zap,
      sectorKey: 'caseStudies.case4Sector',
      titleKey: 'caseStudies.case4Title',
      descKey: 'caseStudies.case4Desc',
      tags: ['Process', 'CRM', 'Formation'],
    },
  ];

  return (
    <div className="bg-[#F5F5F5] min-h-screen">
      <SEO 
        title={currentSeo.title}
        description={currentSeo.description}
        path="/case-studies"
        structuredData={structuredData}
      />
      <Navbar />
      
      {/* Hero Section - Teasing Premium */}
      <section className="pt-24 sm:pt-32 pb-16 sm:pb-32 px-4 sm:px-6 bg-gradient-to-b from-[#041C2C] to-[#0a2a3d] relative overflow-hidden">
        {/* Background decoration */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-20 right-10 w-96 h-96 bg-[#2A41E5]/10 rounded-full blur-3xl" />
          <div className="absolute bottom-20 left-10 w-64 h-64 bg-[#2A41E5]/5 rounded-full blur-2xl" />
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-[#2A41E5]/5 rounded-full blur-3xl" />
        </div>
        
        <div className="max-w-6xl mx-auto relative z-10">
          <AnimatedSection>
            <div className="inline-flex items-center px-4 py-2 rounded-full bg-[#2A41E5]/20 border border-[#2A41E5]/30 mb-8">
              <span className="text-sm text-[#2A41E5] font-medium">{t('caseStudies.badge')}</span>
            </div>
          </AnimatedSection>
          
          <AnimatedSection delay={100}>
            <h1 className="text-2xl sm:text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-bold text-white mb-6 sm:mb-8 leading-tight max-w-5xl">
              {t('caseStudies.heroTitle')}<br />
              <span className="text-[#2A41E5]">{t('caseStudies.heroTitleHighlight')}</span>
            </h1>
          </AnimatedSection>
          
          <AnimatedSection delay={200}>
            <p className="text-xl md:text-2xl text-white/70 max-w-3xl leading-relaxed mb-12">
              {t('caseStudies.heroSubtitle')}
            </p>
          </AnimatedSection>

          <AnimatedSection delay={300}>
            <p className="text-lg text-white/50 italic">
              {t('caseStudies.heroConclusion')}
            </p>
          </AnimatedSection>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-20 px-6 bg-white border-b border-gray-100">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 md:gap-12">
            {stats.map((stat, idx) => (
              <AnimatedSection key={idx} delay={idx * 100}>
                <div className="text-center">
                  <div className="text-4xl md:text-5xl lg:text-6xl font-bold text-[#041C2C] mb-2">
                    <AnimatedCounter end={stat.value} suffix={stat.suffix} />
                  </div>
                  <p className="text-gray-500 text-sm md:text-base">{t(stat.labelKey)}</p>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* Teaser Cards - Coming Soon */}
      <section className="py-12 sm:py-16 md:py-24 px-4 sm:px-6 bg-[#F5F5F5]">
        <div className="max-w-6xl mx-auto">
          <AnimatedSection>
            <div className="text-center mb-16">
              <span className="text-[#2A41E5] font-semibold text-sm uppercase tracking-wider mb-4 block">{t('caseStudies.comingSoonLabel')}</span>
              <h2 className="text-xl sm:text-2xl md:text-3xl lg:text-5xl font-bold text-gray-900 mb-6">
                {t('caseStudies.discoverTitle')}
              </h2>
              <p className="text-lg text-gray-600 max-w-2xl mx-auto">
                {t('caseStudies.discoverSubtitle')}
              </p>
            </div>
          </AnimatedSection>

          <div className="grid md:grid-cols-2 gap-8">
            {teaserCards.map((card, idx) => (
              <AnimatedSection key={idx} delay={idx * 150}>
                <div className="group bg-white rounded-2xl p-8 border border-gray-100 hover:border-[#2A41E5]/30 hover:shadow-xl transition-all duration-500 relative overflow-hidden">
                  {/* Blur overlay - Coming soon */}
                  <div className="absolute inset-0 backdrop-blur-[2px] bg-white/60 z-10 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                    <span className="px-6 py-3 bg-[#041C2C] text-white font-semibold rounded-full text-sm">
                      {t('caseStudies.comingSoon')}
                    </span>
                  </div>
                  
                  {/* Header */}
                  <div className="flex items-start justify-between mb-6">
                    <card.icon className="w-6 h-6 text-[#2A41E5]" />
                    <span className="text-xs font-medium text-gray-400 uppercase tracking-wider">{t(card.sectorKey)}</span>
                  </div>
                  
                  {/* Content */}
                  <h3 className="text-xl font-bold text-gray-900 mb-3">{t(card.titleKey)}</h3>
                  <p className="text-gray-600 mb-6 leading-relaxed">{t(card.descKey)}</p>
                  
                  {/* Tags */}
                  <div className="flex flex-wrap gap-2">
                    {card.tags.map((tag, tagIdx) => (
                      <span key={tagIdx} className="px-3 py-1 bg-gray-100 text-gray-600 text-xs font-medium rounded-full">
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-12 sm:py-16 md:py-24 px-4 sm:px-6 bg-[#041C2C]">
        <div className="max-w-4xl mx-auto text-center">
          <AnimatedSection>
            <h2 className="text-xl sm:text-2xl md:text-3xl lg:text-5xl font-bold text-white mb-6">
              {t('caseStudies.ctaTitle')}
            </h2>
            <p className="text-lg text-white/60 mb-10 max-w-2xl mx-auto">
              {t('caseStudies.ctaSubtitle')}
            </p>
            <a
              href="https://meetings-eu1.hubspot.com/younes8"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-3 px-8 py-4 bg-[#2A41E5] text-white font-bold rounded-xl hover:bg-[#1E32B8] transition-all duration-300 hover:shadow-lg hover:shadow-[#2A41E5]/30 group"
            >
              {t('common.bookCall')}
              <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </a>
          </AnimatedSection>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-10 sm:py-16 px-4 sm:px-6 bg-[#1A1A1A]">
        <div className="max-w-7xl mx-auto flex flex-col items-center justify-center gap-6">
          <Logo variant="white" height={28} />
          <p className="text-gray-400">{t('footer.copyright')} {t('common.allRightsReserved')}.</p>
        </div>
      </footer>
    </div>
  );
};

export default CaseStudies;
